import { FunctionReference } from "convex/server";

type QueryRef = FunctionReference<"query", "public", any, any>;
type MutationRef = FunctionReference<"mutation", "public", any, any>;
type ActionRef = FunctionReference<"action", "public", any, any>;

function createQueryRef(path: string): QueryRef {
  return path as unknown as QueryRef;
}

function createMutationRef(path: string): MutationRef {
  return path as unknown as MutationRef;
}

function createActionRef(path: string): ActionRef {
  return path as unknown as ActionRef;
}

export const convexApi = {
  users: {
    ensureUserSiteAccess: createMutationRef("users:ensureUserSiteAccess"),
  },
  
  organizations: {
    getAll: createQueryRef("organizations:getAll"),
    getAllSites: createQueryRef("organizations:getAllSites"),
    getSiteBySlug: createQueryRef("organizations:getSiteBySlug"),
    getUserSites: createQueryRef("organizations:getUserSites"),
    createOrganizationWithSite: createMutationRef("organizations:createOrganizationWithSite"),
    requestJoinSite: createMutationRef("organizations:requestJoinSite"),
  },

  demo: {
    listDemoSites: createQueryRef("demo:listDemoSites"),
    joinDemoSite: createMutationRef("demo:joinDemoSite"),
  },

  staff: {
    getMyStaffForSite: createQueryRef("staff:getMyStaffForSite"),
    listStaffMembers: createQueryRef("staff:listStaffMembers"),
    getStaffMember: createQueryRef("staff:getStaffMember"),
    getStaffByBarcode: createQueryRef("staff:getStaffByBarcode"),
    createStaffMember: createMutationRef("staff:createStaffMember"),
    updateStaffMember: createMutationRef("staff:updateStaffMember"),
    deleteStaffMember: createMutationRef("staff:deleteStaffMember"),
    regenerateBarcode: createMutationRef("staff:regenerateBarcode"),
    getRosterWeek: createQueryRef("staff:getRosterWeek"),
    listRosterWeeks: createQueryRef("staff:listRosterWeeks"),
    getRosterShifts: createQueryRef("staff:getRosterShifts"),
    createRosterWeek: createMutationRef("staff:createRosterWeek"),
    publishRosterWeek: createMutationRef("staff:publishRosterWeek"),
    unpublishRosterWeek: createMutationRef("staff:unpublishRosterWeek"),
    createShift: createMutationRef("staff:createShift"),
    updateShift: createMutationRef("staff:updateShift"),
    deleteShift: createMutationRef("staff:deleteShift"),
    copyShiftsFromWeek: createMutationRef("staff:copyShiftsFromWeek"),
    getClockEvents: createQueryRef("staff:getClockEvents"),
    getStaffClockEvents: createQueryRef("staff:getStaffClockEvents"),
    clockIn: createMutationRef("staff:clockIn"),
    clockOut: createMutationRef("staff:clockOut"),
    addManualClockEvent: createMutationRef("staff:addManualClockEvent"),
    getAttendanceReport: createQueryRef("staff:getAttendanceReport"),
    getStaffShifts: createQueryRef("staff:getStaffShifts"),
  },
  
  staffRoles: {
    listRoles: createQueryRef("staffRoles:listRoles"),
    getRole: createQueryRef("staffRoles:getRole"),
    getRoleByKey: createQueryRef("staffRoles:getRoleByKey"),
    createRole: createMutationRef("staffRoles:createRole"),
    updateRole: createMutationRef("staffRoles:updateRole"),
    deleteRole: createMutationRef("staffRoles:deleteRole"),
    reorderRoles: createMutationRef("staffRoles:reorderRoles"),
    seedDefaultRoles: createMutationRef("staffRoles:seedDefaultRoles"),
  },
  
  shiftPresets: {
    listBySite: createQueryRef("shiftPresets:listBySite"),
    listActiveBySite: createQueryRef("shiftPresets:listActiveBySite"),
    create: createMutationRef("shiftPresets:create"),
    update: createMutationRef("shiftPresets:update"),
    remove: createMutationRef("shiftPresets:remove"),
    reorder: createMutationRef("shiftPresets:reorder"),
  },
  
  staffAvailability: {
    getPatternsByStaff: createQueryRef("staffAvailability:getPatternsByStaff"),
    getPatternsBySite: createQueryRef("staffAvailability:getPatternsBySite"),
    upsertPattern: createMutationRef("staffAvailability:upsertPattern"),
    setWeeklyPattern: createMutationRef("staffAvailability:setWeeklyPattern"),
    deletePattern: createMutationRef("staffAvailability:deletePattern"),
    getOverridesByStaff: createQueryRef("staffAvailability:getOverridesByStaff"),
    getOverridesBySiteAndDateRange: createQueryRef("staffAvailability:getOverridesBySiteAndDateRange"),
    createOverride: createMutationRef("staffAvailability:createOverride"),
    deleteOverride: createMutationRef("staffAvailability:deleteOverride"),
    saveWeekOverrides: createMutationRef("staffAvailability:saveWeekOverrides"),
    getStaffAvailabilityForDateRange: createQueryRef("staffAvailability:getStaffAvailabilityForDateRange"),
    getAllStaffAvailabilityForWeek: createQueryRef("staffAvailability:getAllStaffAvailabilityForWeek"),
  },
  
  leaveRequests: {
    getByStaff: createQueryRef("leaveRequests:getByStaff"),
    getBySite: createQueryRef("leaveRequests:getBySite"),
    getPending: createQueryRef("leaveRequests:getPending"),
    getById: createQueryRef("leaveRequests:getById"),
    create: createMutationRef("leaveRequests:create"),
    approve: createMutationRef("leaveRequests:approve"),
    reject: createMutationRef("leaveRequests:reject"),
    cancel: createMutationRef("leaveRequests:cancel"),
  },
  
  staffInvites: {
    getBySite: createQueryRef("staffInvites:getBySite"),
    getByCode: createQueryRef("staffInvites:getByCode"),
    getActiveInvite: createQueryRef("staffInvites:getActiveInvite"),
    create: createMutationRef("staffInvites:create"),
    use: createMutationRef("staffInvites:use"),
    expire: createMutationRef("staffInvites:expire"),
  },

  staffJoin: {
    checkEmailInviteOnLogin: createQueryRef("staffJoin:checkEmailInviteOnLogin"),
    acceptEmailInvite: createMutationRef("staffJoin:acceptEmailInvite"),
    requestToJoinSite: createMutationRef("staffJoin:requestToJoinSite"),
    getStaffNeedingAccount: createQueryRef("staffJoin:getStaffNeedingAccount"),
    repairMyAccountLinks: createMutationRef("staffJoin:repairMyAccountLinks"),
  },
  
  shiftRequests: {
    getByStaff: createQueryRef("shiftRequests:getByStaff"),
    getById: createQueryRef("shiftRequests:getById"),
    create: createMutationRef("shiftRequests:create"),
    approve: createMutationRef("shiftRequests:approve"),
    reject: createMutationRef("shiftRequests:reject"),
    cancel: createMutationRef("shiftRequests:cancel"),
  },
  
  shiftSwapRequests: {
    getBySite: createQueryRef("shiftSwapRequests:getBySite"),
    getPending: createQueryRef("shiftSwapRequests:getPending"),
    getById: createQueryRef("shiftSwapRequests:getById"),
    getPendingForShift: createQueryRef("shiftSwapRequests:getPendingForShift"),
    getByStaff: createQueryRef("shiftSwapRequests:getByStaff"),
    create: createMutationRef("shiftSwapRequests:create"),
    respondAsTarget: createMutationRef("shiftSwapRequests:respondAsTarget"),
    approveAsManager: createMutationRef("shiftSwapRequests:approveAsManager"),
    rejectAsManager: createMutationRef("shiftSwapRequests:rejectAsManager"),
    cancel: createMutationRef("shiftSwapRequests:cancel"),
  },
  
  siteSettings: {
    get: createQueryRef("siteSettings:get"),
    update: createMutationRef("siteSettings:update"),
  },
  
  clockIn: {
    getSettings: createQueryRef("clockIn:getSettings"),
    clockInByQr: createMutationRef("clockIn:clockInByQr"),
    clockInByPin: createMutationRef("clockIn:clockInByPin"),
    updateSettings: createMutationRef("clockIn:updateSettings"),
    setStaffPin: createMutationRef("clockIn:setStaffPin"),
    getRecentEvents: createQueryRef("clockIn:getRecentEvents"),
    getMyEvents: createQueryRef("clockIn:getMyEvents"),
  },
  
  clockInCrypto: {
    generateKioskQr: createActionRef("clockInCrypto:generateKioskQr"),
    verifyQrToken: createActionRef("clockInCrypto:verifyQrToken"),
  },
  
  storage: {
    getUrl: createQueryRef("storage:getUrl"),
    getUrls: createQueryRef("storage:getUrls"),
    generateUploadUrl: createMutationRef("storage:generateUploadUrl"),
  },
  
  staffComms: {
    sendAnonymousTip: createMutationRef("staffComms:sendAnonymousTip"),
    getTips: createQueryRef("staffComms:getTips"),
    resolveTip: createMutationRef("staffComms:resolveTip"),
    createDirectThread: createMutationRef("staffComms:createDirectThread"),
    createGroupThread: createMutationRef("staffComms:createGroupThread"),
    sendDirectMessage: createMutationRef("staffComms:sendDirectMessage"),
    getDirectThreads: createQueryRef("staffComms:getDirectThreads"),
    getThreadMessages: createQueryRef("staffComms:getThreadMessages"),
    markThreadRead: createMutationRef("staffComms:markThreadRead"),
    createHandoverNote: createMutationRef("staffComms:createHandoverNote"),
    getHandoverNotes: createQueryRef("staffComms:getHandoverNotes"),
    clearHandoverNotes: createMutationRef("staffComms:clearHandoverNotes"),
    hasUnclearedHandovers: createQueryRef("staffComms:hasUnclearedHandovers"),
    markHandoverRead: createMutationRef("staffComms:markHandoverRead"),
    updateHandoverNote: createMutationRef("staffComms:updateHandoverNote"),
    createContextThread: createMutationRef("staffComms:createContextThread"),
    sendContextMessage: createMutationRef("staffComms:sendContextMessage"),
    getThreadsByContext: createQueryRef("staffComms:getThreadsByContext"),
    markContextThreadRead: createMutationRef("staffComms:markContextThreadRead"),
    getAllThreadsForUser: createQueryRef("staffComms:getAllThreadsForUser"),
  },
  
  notifications: {
    registerDeviceToken: createMutationRef("notifications:registerDeviceToken"),
    unregisterDeviceToken: createMutationRef("notifications:unregisterDeviceToken"),
    getMyDeviceTokens: createQueryRef("notifications:getMyDeviceTokens"),
    getNotificationLogs: createQueryRef("notifications:getNotificationLogs"),
  },
  
  notificationPreferences: {
    get: createQueryRef("notificationPreferences:get"),
    upsert: createMutationRef("notificationPreferences:upsert"),
  },
  
  siteBackup: {
    getImportStatus: createQueryRef("siteBackup:getImportStatus"),
    setImportingState: createMutationRef("siteBackup:setImportingState"),
    forceReleaseImportLock: createMutationRef("siteBackup:forceReleaseImportLock"),
  },
  
  projects: {
    listProjects: createQueryRef("projects:listProjects"),
    getProject: createQueryRef("projects:getProject"),
    getProjectTask: createQueryRef("projects:getProjectTask"),
    createProject: createMutationRef("projects:createProject"),
    updateProject: createMutationRef("projects:updateProject"),
    deleteProject: createMutationRef("projects:deleteProject"),
    updateProjectProgress: createMutationRef("projects:updateProjectProgress"),
    addProjectMember: createMutationRef("projects:addProjectMember"),
    removeProjectMember: createMutationRef("projects:removeProjectMember"),
    listProjectTasks: createQueryRef("projects:listProjectTasks"),
    createProjectTask: createMutationRef("projects:createProjectTask"),
    updateProjectTask: createMutationRef("projects:updateProjectTask"),
    completeProjectTask: createMutationRef("projects:completeProjectTask"),
    listProjectTaskChecklistItems: createQueryRef("projects:listProjectTaskChecklistItems"),
    completeProjectTaskChecklistItem: createMutationRef("projects:completeProjectTaskChecklistItem"),
    getGanttData: createQueryRef("projects:getGanttData"),
    listProjectNotes: createQueryRef("projects:listProjectNotes"),
  },
  
  training: {
    listCourses: createQueryRef("training:listCourses"),
    getCourse: createQueryRef("training:getCourse"),
    getMyCourses: createQueryRef("training:getMyCourses"),
    getMyCompletions: createQueryRef("training:getMyCompletions"),
    getQuiz: createQueryRef("training:getQuiz"),
    startCourse: createMutationRef("training:startCourse"),
    completeModule: createMutationRef("training:completeModule"),
    recordModuleProgress: createMutationRef("training:recordModuleProgress"),
    completeCourse: createMutationRef("training:completeCourse"),
    submitQuizAttempt: createMutationRef("training:submitQuizAttempt"),
    getMyBadges: createQueryRef("training:getMyBadges"),
  },
  
  procedures: {
    listProcedures: createQueryRef("procedures:listProcedures"),
    getProcedure: createQueryRef("procedures:getProcedure"),
    listProcedureLogs: createQueryRef("procedures:listProcedureLogs"),
    getDueProcedures: createQueryRef("procedures:getDueProcedures"),
    startProcedure: createMutationRef("procedures:startProcedure"),
    updateStepResponse: createMutationRef("procedures:updateStepResponse"),
    completeProcedure: createMutationRef("procedures:completeProcedure"),
    failProcedure: createMutationRef("procedures:failProcedure"),
  },
  
  meetings: {
    listMyMeetings: createQueryRef("meetings:listMyMeetings"),
    list: createQueryRef("meetings:list"),
    getUpcoming: createQueryRef("meetings:getUpcoming"),
    getPendingInvites: createQueryRef("meetings:getPendingInvites"),
    getById: createQueryRef("meetings:getById"),
    getMeeting: createQueryRef("meetings:getMeeting"),
    create: createMutationRef("meetings:create"),
    update: createMutationRef("meetings:update"),
    cancel: createMutationRef("meetings:cancel"),
    respondToInvite: createMutationRef("meetings:respondToInvite"),
    addInvitees: createMutationRef("meetings:addInvitees"),
    createMeeting: createMutationRef("meetings:createMeeting"),
    updateMeeting: createMutationRef("meetings:updateMeeting"),
    deleteMeeting: createMutationRef("meetings:deleteMeeting"),
    rsvpToMeeting: createMutationRef("meetings:rsvpToMeeting"),
  },
  
  tasks: {
    listTasks: createQueryRef("tasks:listTasks"),
    listMyTasks: createQueryRef("tasks:listMyTasks"),
    listAllTasks: createQueryRef("tasks:listTasks"),
    listByDate: createQueryRef("tasks:listByDate"),
    getTask: createQueryRef("tasks:getTask"),
    getTaskChecklistItems: createQueryRef("tasks:getTaskChecklistItems"),
    startTask: createMutationRef("tasks:startTask"),
    completeTask: createMutationRef("tasks:completeTask"),
    completeChecklistItem: createMutationRef("tasks:completeChecklistItem"),
    createTask: createMutationRef("tasks:createTask"),
    updateTask: createMutationRef("tasks:updateTask"),
    addTaskPhoto: createMutationRef("tasks:addTaskPhoto"),
    toggleChecklistItem: createMutationRef("tasks:toggleChecklistItem"),
  },
  
  reminders: {
    listMyReminders: createQueryRef("calendar:listMyReminders"),
    getUpcomingReminders: createQueryRef("calendar:getUpcomingReminders"),
    createReminder: createMutationRef("calendar:createReminder"),
    updateReminder: createMutationRef("calendar:updateReminder"),
    deleteReminder: createMutationRef("calendar:deleteReminder"),
    dismissReminder: createMutationRef("calendar:dismissReminder"),
    snoozeReminder: createMutationRef("calendar:snoozeReminder"),
  },
  
  calendar: {
    getCalendarEvents: createQueryRef("calendar:getCalendarEvents"),
    listMyEvents: createQueryRef("calendar:listMyEvents"),
    getCalendarDayData: createQueryRef("calendar:getCalendarDayData"),
    getCalendarWeekData: createQueryRef("calendar:getCalendarWeekData"),
    getCalendarMonthData: createQueryRef("calendar:getCalendarMonthData"),
    createEvent: createMutationRef("calendar:createEvent"),
    updateEvent: createMutationRef("calendar:updateEvent"),
    deleteEvent: createMutationRef("calendar:deleteEvent"),
  },
  
  messaging: {
    listBoards: createQueryRef("messaging:listBoards"),
    listBoardMessages: createQueryRef("messaging:listBoardMessages"),
  },

  siteFeatures: {
    get: createQueryRef("siteFeatures:get"),
    isEnabled: createQueryRef("siteFeatures:isEnabled"),
    update: createMutationRef("siteFeatures:update"),
    toggleFeature: createMutationRef("siteFeatures:toggleFeature"),
    initializeForSite: createMutationRef("siteFeatures:initializeForSite"),
    toggleNoticeboardStaffCanPost: createMutationRef("siteFeatures:toggleNoticeboardStaffCanPost"),
  },
};
